import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import { Autocomplete, CircularProgress, TableContainer, TablePagination, TextField } from "@mui/material";
import { RestartAlt } from "@mui/icons-material";
import { getSubCategories, deleteSubCategory, updateSubCategory } from ".";
import { getCategories } from "../video";
import { StyledTableCell, StyledTableRow } from '../../utils/constantData'
import { resolveMediaUrl } from "../../utils/mediaUrl";
import { AdminPageLayout, AdminSectionCard, TableRowActions, DeleteConfirmationDialog, AdminAction } from "../../components/admin";
import { adminResetButtonSx } from "../../components/admin/adminButtonStyles";

export default function SubCategory() {
  const [video, setVideo] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [data, setData] = React.useState([]);
  const [load, setLoad] = React.useState(null);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [id, setId] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [filterKey, setFilterKey] = React.useState(0);
  const [searchInput, setSearchInput] = React.useState("");

  const hasActiveFilters = id !== 1 || !!searchInput.trim();

  const startIndex = page * rowsPerPage;
  const location = useLocation();
  const { results } = location.state || {};
  const navigate = useNavigate();
  const { update } = location.state || {};

  React.useEffect(() => {
    if (results) {
      toast.success("SubCategory Added successfully!", {
        toastId: 'success6',
        position: "bottom-right",
        draggable: true,
        hideProgressBar: false,
        closeOnClick: true,
        autoClose: 2000,
        pauseOnHover: true,
        progress: undefined,
        theme: "light",
      });
    } else if (update) {
      toast.success("SubCategory Updated successfully!", {
        hideProgressBar: false,
        draggable: true,
        toastId: 'success7',
        position: "bottom-right",
        autoClose: 2000,
        closeOnClick: true,
        pauseOnHover: true,
        progress: undefined,
        theme: "light",
      });
    }
  }, []);

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await getSubCategories(id);
      const responseData = response.data;
      setLoading(false)
      setVideo(responseData?.data || []);
    } catch (error) {
      setLoading(false);
      toast.error(error.response?.data?.message || "Failed to load sub categories", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
      console.log(error);
    }
  };

  const fetchCategory = async () => {
    try {
      const response = await getCategories();
      const responseData = response.data;
      setData(responseData?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load categories", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
      console.log(error);
    }
  };

  const filteredRows = React.useMemo(() => {
    const query = searchInput.trim().toLowerCase();
    if (!query) return video;
    return video.filter((row) => (row.name || "").toLowerCase().includes(query));
  }, [video, searchInput]);

  React.useEffect(() => {
    fetchData();
  }, [id, load]);

  React.useEffect(() => {
    fetchCategory();
  }, []);

  React.useEffect(() => {
    setPage(0);
  }, [id, searchInput]);

  const handleResetFilters = () => {
    setSearchInput("");
    setId(1);
    setPage(0);
    setFilterKey((k) => k + 1);
  };

  const navigateToAddSubCategory = () => {
    navigate(`/subCategory/add`);
  };

  const handleEdit = React.useCallback(async (id) => {
    navigate(`/subCategory/${id}`)
  }, [])

  const handleDelete = React.useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await deleteSubCategory(deleteTarget.id);
      if (result) {
        setLoad("ff");
        toast.success("SubCategory Deleted Successfully!", {
          toastId: 'success1',
          position: "bottom-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        setDeleteTarget(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete sub category", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget])

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleCategory = (value) => {
    setPage(0);
    if (value) setId(value.id);
    else {
      setId("");
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleStatus = async (nextValue, id) => {
    try {
      const formData = new FormData();
      formData.append("active", nextValue)
      const response = await updateSubCategory(id, formData);
      if (response) {
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update sub category status", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
      console.log(error);
    }
  }

  return (
    <AdminPageLayout
      eyebrow="Content Structure"
      title="Sub Categories"
      description="Category groupings with cleaner controls for status, filtering, and content edits."
      actions={
        <div className="admin-toolbar" style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <TextField
            key={`search-${filterKey}`}
            id="subcategory-search"
            label="Search sub categories"
            value={searchInput}
            size="small"
            sx={{ minWidth: 220 }}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(0);
            }}
          />
          <Autocomplete
            key={`cat-${filterKey}`}
            disablePortal
            id="category"
            options={data}
            defaultValue={{ id: 1, name: "Agriculture" }}
            getOptionLabel={(option) => option?.name || ""}
            onChange={(event, newValue) => handleCategory(newValue)}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            size="small"
            sx={{ minWidth: 220 }}
            renderInput={(params) => <TextField {...params} label="Category" />}
          />
          <AdminAction
            id="reset-filters-btn"
            variant="outline"
            startIcon={<RestartAlt />}
            disabled={!hasActiveFilters}
            onClick={handleResetFilters}
            sx={{ whiteSpace: "nowrap", ...adminResetButtonSx(hasActiveFilters) }}
          >
            Reset
          </AdminAction>
          <AdminAction onClick={navigateToAddSubCategory}>Add SubCategory</AdminAction>
        </div>
      }
    >
      <AdminSectionCard
        title="Sub category list"
        description="Filter by parent category and manage each sub category from one consistent admin table."
        className="admin-table-shell"
      >
        <TableContainer component="div">
          <Table sx={{ minWidth: 700 }} aria-label="subcategories table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>Sr. No.</StyledTableCell>
                <StyledTableCell>Image</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Created By</StyledTableCell>
                <StyledTableCell align="right">Actions</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length !== 0 ? (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <StyledTableRow key={row.id}>
                      <StyledTableCell component="th">
                        {startIndex + index + 1}
                      </StyledTableCell>
                      <StyledTableCell>
                        <img
                          src={row.imageUrl ? resolveMediaUrl(row.imageUrl) : "/placeholder-image.jpg"}
                          alt={row.name || "Sub category"}
                          className="h-12 w-12 rounded object-cover bg-zinc-100"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/placeholder-image.jpg";
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <div className="font-semibold text-[15px] text-[#172112]">{row.name}</div>
                      </StyledTableCell>
                      <StyledTableCell>
                        {row.creator?.fullName || "—"}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <TableRowActions
                          rowId={row.id}
                          onEdit={() => handleEdit(row.id)}
                          onDelete={() => setDeleteTarget(row)}
                          status={{
                            isActive: row.active === 1,
                            onToggle: () => handleStatus(row.active === 1 ? 0 : 1, row.id, row.name),
                          }}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
              ) : loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-8">
                    <CircularProgress size={20} className="text-zinc-500" />
                    <span className="ml-2 text-zinc-500">Loading...</span>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-zinc-500">
                    No sub categories found.
                  </td>
                </tr>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </AdminSectionCard>

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Sub Category"
        itemName={deleteTarget?.name}
        loading={deleting}
      />
    </AdminPageLayout>
  );
}
